import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { GhostBtn } from 'common/components/molecules/ListToolbar';
import {
  INK, INK_2, INK_3, LINE, LINE_2, BG, GREEN, GREEN_T, GREEN_LINE, BLUE, BLUE_T, BLUE_LINE,
  AMBER, AMBER_T, AMBER_LINE, CRITICAL, RADIUS, THIN_SCROLLBAR
} from 'common/styles/listUI';
import { NOTE_STATUSES, NOTE_TYPES } from '../constants/releaseNotes.constants';

// The colour language shared by the reader, the manage page and the editor:
// features green, improvements blue, fixes amber; published green, scheduled
// blue, drafts amber.
const GREEN_TONE = { ink: GREEN, tint: GREEN_T, line: GREEN_LINE };
const BLUE_TONE = { ink: BLUE, tint: BLUE_T, line: BLUE_LINE };
const AMBER_TONE = { ink: AMBER, tint: AMBER_T, line: AMBER_LINE };
const NEUTRAL_TONE = { ink: INK_2, tint: LINE_2, line: LINE };

const TYPE_TONES = {
  [NOTE_TYPES.FEATURE]: GREEN_TONE,
  [NOTE_TYPES.IMPROVEMENT]: BLUE_TONE,
  [NOTE_TYPES.FIX]: AMBER_TONE
};

const STATUS_TONES = {
  [NOTE_STATUSES.PUBLISHED]: GREEN_TONE,
  [NOTE_STATUSES.SCHEDULED]: BLUE_TONE,
  [NOTE_STATUSES.DRAFT]: AMBER_TONE
};

export const typeTone = type => TYPE_TONES[type] || NEUTRAL_TONE;
export const statusTone = status => STATUS_TONES[status] || NEUTRAL_TONE;

// A pill's tone is fixed (a type or a status) or the brand's, picked from the
// domain's theme (brandTone).
const toneOf = props => (typeof props.tone === 'function' ? props.tone(props.theme) : props.tone);

export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 9px;
  border-radius: ${RADIUS.pill};
  border: 1px solid ${props => toneOf(props).line};
  background: ${props => toneOf(props).tint};
  color: ${props => toneOf(props).ink};
  font-size: 11.5px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
`;

export const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.tone.ink};
  flex: 0 0 auto;
`;

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: ${BG};
  color: ${INK};
`;

export const ToolbarRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
  padding: 0 20px;
  background: white;
  border-bottom: 1px solid ${LINE};
  flex: 0 0 auto;
`;

export const ToolbarTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 14px;
  margin-right: 4px;
  border-right: 1px solid ${LINE};
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
`;

export const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  padding: 8px 0;
`;

export const ScrollBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  ${THIN_SCROLLBAR}
`;

export const Muted = styled.span`
  color: ${INK_2};
`;

export const Faint = styled.span`
  color: ${INK_3};
`;

export const StateBox = styled.div`
  max-width: 420px;
  margin: 80px auto;
  text-align: center;
  color: ${INK_2};
  font-size: 13px;

  h3 {
    margin: 0 0 6px;
    font-size: 15px;
    color: ${INK};
  }
`;

export const TextButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${props => (props.danger ? CRITICAL : props.theme.primaryColor)};
  cursor: pointer;

  &:hover { text-decoration: underline; }
  &:disabled { color: ${INK_3}; cursor: default; text-decoration: none; }
`;

// The toolbar's ghost button, as a router link.
export const GhostLink = GhostBtn.withComponent(Link);
